import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFileUpload } from './useFileUpload'
import { getPopulatedPrompt } from '@/utils/promptUtils'

export interface FormValues {
  decisionNeeded: string;
  contextAndGoals: string;
  optionsConsidered: string;
  constraints: string;
  outputFormat: string;
  referenceFiles: string;
}

const initialValues: FormValues = {
  decisionNeeded: '',
  contextAndGoals: '',
  optionsConsidered: '',
  constraints: '',
  outputFormat: '',
  referenceFiles: ''
}

export const usePromptForm = (promptTemplate: string) => {
  const navigate = useNavigate()
  const [step, setStep] = useState(0)
  const [showPreview, setShowPreview] = useState(false)
  const [finalPrompt, setFinalPrompt] = useState('')
  const [values, setValues] = useState<FormValues>(initialValues)
  const { uploading, uploadedFiles, handleFileUpload, removeFile, setUploadedFiles } = useFileUpload()

  const handleInputChange = (field: keyof FormValues, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleInputFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const uploadedFile = await handleFileUpload(file)
      if (uploadedFile) {
        const filesList = [...uploadedFiles, uploadedFile].map(f => f.name).join('\n')
        handleInputChange('referenceFiles', filesList)
      }
    }
  }

  const handleRemoveFile = (fileToRemove: { name: string; url: string }) => {
    removeFile(fileToRemove)
    const filesList = uploadedFiles
      .filter(file => file.name !== fileToRemove.name)
      .map(f => f.name)
      .join('\n')
    handleInputChange('referenceFiles', filesList)
  }

  const handleNext = () => {
    if (step < 3) {
      setStep(step + 1)
    } else {
      const populatedPrompt = getPopulatedPrompt(values, uploadedFiles, promptTemplate)
      setFinalPrompt(populatedPrompt)
      setShowPreview(true)
    }
  }

  const handleUpdatePrompt = (newPrompt: string) => {
    setFinalPrompt(newPrompt)
  }

  const handleProceed = () => {
    navigate('/chat-with-claude', { 
      state: { prompt: finalPrompt } 
    })
  }

  const resetForm = () => {
    setStep(0)
    setShowPreview(false)
    setValues(initialValues)
    setUploadedFiles([])
  }

  return {
    step,
    showPreview,
    finalPrompt,
    values,
    uploading,
    uploadedFiles,
    handleInputChange,
    handleInputFileChange,
    handleRemoveFile,
    handleNext,
    handleUpdatePrompt,
    handleProceed,
    resetForm
  }
}